// Split list-item AND unstyled blocks whose text contains '\n' into multiple
// sibling blocks of the same type, one per line. Recalculates inlineStyleRanges
// and entityRanges so styles stay attached to the correct segment. Idempotent:
// blocks without '\n' pass through unchanged.
//
// `unstyled` is included because AI-generated ASSESSMENT/PLAN sections sometimes
// cram multiple lines (e.g. a stack of ICD-10 rows) into one block separated by
// '\n' — those collapse to whitespace in HTML otherwise and render as one long
// line.

const sliceRanges = (ranges, segStart, segEnd) => {
  if (!Array.isArray(ranges) || ranges.length === 0) return [];
  const result = [];
  for (const r of ranges) {
    const rStart = (r && r.offset) || 0;
    const rEnd = rStart + ((r && r.length) || 0);
    const overlapStart = Math.max(rStart, segStart);
    const overlapEnd = Math.min(rEnd, segEnd);
    if (overlapEnd <= overlapStart) continue;
    result.push({
      ...r,
      offset: overlapStart - segStart,
      length: overlapEnd - overlapStart,
    });
  }
  return result;
};

const splitBlock = (block) => {
  const text = (block && block.text) || "";
  if (!text.includes("\n")) return [block];

  const segments = text.split("\n");
  const blocks = [];
  let cursor = 0;

  segments.forEach((segment, idx) => {
    const segStart = cursor;
    const segEnd = cursor + segment.length;
    blocks.push({
      ...block,
      key: idx === 0 ? block.key : `${block.key || "split"}_n${idx}`,
      text: segment,
      inlineStyleRanges: sliceRanges(block.inlineStyleRanges, segStart, segEnd),
      entityRanges: sliceRanges(block.entityRanges, segStart, segEnd),
    });
    cursor = segEnd + 1; // +1 for the '\n' character we split on
  });

  return blocks;
};

const SPLITTABLE_TYPES = ["unordered-list-item", "ordered-list-item", "unstyled"];

const splitListItemNewlines = (dataMix) => {
  if (!dataMix || !dataMix.blocks || !Array.isArray(dataMix.blocks)) return dataMix;

  const flattened = [];
  for (const block of dataMix.blocks) {
    const isSplittable = block && SPLITTABLE_TYPES.includes(block.type);
    if (isSplittable && typeof block.text === "string" && block.text.includes("\n")) {
      for (const b of splitBlock(block)) flattened.push(b);
    } else {
      flattened.push(block);
    }
  }
  dataMix.blocks = flattened;
  return dataMix;
};

export default splitListItemNewlines;
